import React, { ChangeEvent, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import useFetcher from '../../../../utils/hooks/useFetcher';
import Form from '../../../Forms/form';
import { FormGroup, FormTypeEnum } from '../../../Forms/formInterfaces';
import {
    SurveyDefaultDatePeriod,
    SurveyDefaultDatePeriodToLabel,
    SurveyStatus,
    SurveyStatusToLabel,
    SurveyTemporalFilterType,
    SurveyTemporalFilterTypeToLabel,
} from '../../../Surveys/surveyInterface';
import { TenantInterface } from '../../../Tenants/tenantsInterface';
import { PersonalizedSurveyProps } from '../addSurveyInterface';

interface StepPersonalizeSurveyInterface {
    personalizedSurvey?: PersonalizedSurveyProps
    handlePersonalizedSurvey: (survey: PersonalizedSurveyProps) => void
    handleNextStep: (formId: string) => void
    isEditMode?: boolean
}

const StepPersonalizeSurvey: FC<StepPersonalizeSurveyInterface> = ({ personalizedSurvey, handlePersonalizedSurvey, handleNextStep, isEditMode = false }) => {
    const { t } = useTranslation('settings');
    const fetcher = useFetcher();
    const [surveyContextValue, setSurveyContextValue] = useState<string>(personalizedSurvey ? personalizedSurvey.context : '');
    const [surveyName, setSurveyName] = useState<string>(personalizedSurvey ? personalizedSurvey.name : '');
    const [surveyTemporalFilterType, setSurveyTemporalFilterType] = useState<SurveyTemporalFilterType>(personalizedSurvey ? personalizedSurvey.temporalFilterType : SurveyTemporalFilterType.VAGUE);
    const [surveyDefaultDatePeriod, setSurveyDefaultDatePeriod] = useState<SurveyDefaultDatePeriod>(personalizedSurvey?.defaultDatePeriod ?? SurveyDefaultDatePeriod.CURRENT_MONTH);
    const [surveyClient, setSurveyClient] = useState<string>(personalizedSurvey ? personalizedSurvey.schema : '');
    const [surveyStatus, setSurveyStatus] = useState<SurveyStatus>(personalizedSurvey ? personalizedSurvey.status : SurveyStatus.IN_PREPARATION);
    const [surveyHasDashboard, setSurveyHasDashboard] = useState<boolean>(personalizedSurvey ? personalizedSurvey.hasDashboard : true);
    const [surveyHasHotAlert, setSurveyHasHotAlert] = useState<boolean>(personalizedSurvey ? personalizedSurvey.hasHotAlert : false);
    const [surveyAnonymateDays, setSurveyAnonymateDays] = useState<number | undefined>(personalizedSurvey ? personalizedSurvey.anonymateDays : undefined);
    const [surveyHasToAnonymateData, setSurveyHasToAnonymateData] = useState<boolean>(personalizedSurvey ? personalizedSurvey.hasToAnonymateData : false);
    const [surveyHasAutomaticSynchronization, setSurveyHasAutomaticSynchronization] = useState<boolean>(personalizedSurvey ? personalizedSurvey.hasAutomaticSynchronization : false);
    const { data: fetchedTenants } = useSWR(`${process.env.REACT_APP_API_URL}/tenant`, fetcher);
    const orderedTenants: TenantInterface[] = fetchedTenants && [...fetchedTenants].sort((a: {label: string}, b: {label: string}) => a.label.localeCompare(b.label));
    const handleChangeSurveyContext = (e: ChangeEvent<HTMLInputElement>) => {
        setSurveyContextValue(e.target.value);
        handlePersonalizedSurvey(
            {
                ...personalizedSurvey,
                context: e.target.value,
            } as PersonalizedSurveyProps,
        );
    };

    const handleChangeSurveyName = (e: ChangeEvent<HTMLInputElement>) => {
        setSurveyName(e.target.value);
        handlePersonalizedSurvey(
            {
                ...personalizedSurvey,
                name: e.target.value,
            } as PersonalizedSurveyProps,
        );
    };

    const handleChangeSurveyClient = (choice: {id: string, label: string}) => {
        setSurveyClient(choice.id);
        handlePersonalizedSurvey(
            {
                ...personalizedSurvey,
                schema: choice.id,
            } as PersonalizedSurveyProps,
        );
    };

    const handleChangeSurveyStatus = (choice: {id: SurveyStatus, label: string}) => {
        setSurveyStatus(choice.id);
        handlePersonalizedSurvey(
            {
                ...personalizedSurvey,
                status: choice.id,
            } as PersonalizedSurveyProps,
        );
    };

    const handleChangeSurveyTemporalType = (choice: {id: SurveyTemporalFilterType, label: string}) => {
        setSurveyTemporalFilterType(choice.id);
        handlePersonalizedSurvey(
            {
                ...personalizedSurvey,
                temporalFilterType: choice.id,
            } as PersonalizedSurveyProps,
        );
    };

    const handleChangeSurveyDefaultDatePeriod = (choice: {id: SurveyDefaultDatePeriod, label: string}) => {
        setSurveyDefaultDatePeriod(choice.id);
        handlePersonalizedSurvey(
            {
                ...personalizedSurvey,
                defaultDatePeriod: choice.id,
            } as PersonalizedSurveyProps,
        );
    };

    const handleChangeSurveyHasDashboard = (choice: {id: boolean, label: string}) => {
        setSurveyHasDashboard(choice.id);
        handlePersonalizedSurvey(
            {
                ...personalizedSurvey,
                hasDashboard: choice.id,
            } as PersonalizedSurveyProps,
        );
    };

    const handleChangeSurveyHasHotAlert = (choice: {id: boolean, label: string}) => {
        setSurveyHasHotAlert(choice.id);
        handlePersonalizedSurvey(
            {
                ...personalizedSurvey,
                hasHotAlert: choice.id,
            } as PersonalizedSurveyProps,
        );
    };

    const handleChangeSurveyHasToAnonymateData = (choice: {id: boolean, label: string}) => {
        setSurveyHasToAnonymateData(choice.id);
        handlePersonalizedSurvey(
            {
                ...personalizedSurvey,
                hasToAnonymateData: choice.id,
            } as PersonalizedSurveyProps,
        );
    };

    const handleChangeSurveyHasAutomaticSynchronization = (choice: {id: boolean, label: string}) => {
        setSurveyHasAutomaticSynchronization(choice.id);
        handlePersonalizedSurvey(
            {
                ...personalizedSurvey,
                hasAutomaticSynchronization: choice.id,
            } as PersonalizedSurveyProps,
        );
    };

    const handleChangeSurveyAnonymateDays = (e: ChangeEvent<HTMLInputElement>) => {
        setSurveyAnonymateDays(Number(e.target.value));
        handlePersonalizedSurvey(
            {
                ...personalizedSurvey,
                anonymateDays: Number(e.target.value),
            } as PersonalizedSurveyProps,
        );
    };

    const formGroups: FormGroup[] = [
        {
            id: 'surveyTitle',
            label: '',
            placeholder: '',
            value: surveyName,
            onChange: handleChangeSurveyName,
            type: FormTypeEnum.EDIT_INPUT,
            display: true,
            column: 1,
            isOptional: false,
        },
        {
            id: 'surveyClient',
            label: t('surveys.modal.createSurveyForm.surveyClient'),
            placeholder: t('surveys.modal.createSurveyForm.surveyClientPlaceholder'),
            value: surveyClient,
            onChange: handleChangeSurveyClient,
            type: FormTypeEnum.SELECT_LIST,
            choices: orderedTenants ? orderedTenants.map((tenant: TenantInterface) => ({ id: tenant.code, label: tenant.label })) : [],
            // The survey tables live in the client schema: it cannot be changed once created
            disabled: isEditMode,
            display: true,
            column: 1,
            isOptional: false,
        },
        {
            // Only editable on an existing survey: a new survey always starts IN_PREPARATION
            id: 'surveyStatus',
            label: t('surveys.modal.createSurveyForm.surveyStatus'),
            placeholder: t('surveys.modal.createSurveyForm.surveyStatusPlaceholder'),
            value: surveyStatus,
            onChange: handleChangeSurveyStatus,
            type: FormTypeEnum.SELECT_LIST,
            choices: Object.values(SurveyStatus).map((status) => ({ id: status, label: SurveyStatusToLabel[status] })),
            display: isEditMode,
            column: 1,
            isOptional: false,
        },
        {
            id: 'surveyContext',
            label: t('surveys.modal.createSurveyForm.surveyContext'),
            placeholder: t('surveys.modal.createSurveyForm.surveyContextPlaceholder'),
            value: surveyContextValue,
            onChange: handleChangeSurveyContext,
            type: FormTypeEnum.TEXTAREA,
            display: true,
            column: 1,
            isOptional: false,
        },
        {
            id: 'surveyTemporalType',
            label: t('surveys.modal.createSurveyForm.surveyTemporalType'),
            placeholder: undefined,
            value: surveyTemporalFilterType,
            onChange: handleChangeSurveyTemporalType,
            type: FormTypeEnum.RADIO_SELECT,
            choices: [
                { id: SurveyTemporalFilterType.VAGUE, label: SurveyTemporalFilterTypeToLabel(SurveyTemporalFilterType.VAGUE) },
                { id: SurveyTemporalFilterType.DATE, label: SurveyTemporalFilterTypeToLabel(SurveyTemporalFilterType.DATE) },
            ],
            display: true,
            column: 1,
            isOptional: false,
        },
        {
            id: 'surveyDefaultDatePeriod',
            label: t('surveys.modal.createSurveyForm.surveyDefaultDatePeriod'),
            placeholder: undefined,
            value: surveyDefaultDatePeriod,
            onChange: handleChangeSurveyDefaultDatePeriod,
            type: FormTypeEnum.SELECT_LIST,
            choices: [
                SurveyDefaultDatePeriod.CURRENT_DAY,
                SurveyDefaultDatePeriod.CURRENT_MONTH,
                SurveyDefaultDatePeriod.CURRENT_TRIM,
                SurveyDefaultDatePeriod.CURRENT_YEAR,
                SurveyDefaultDatePeriod.ALL,
            ].map((defaultDatePeriod) => ({ id: defaultDatePeriod, label: SurveyDefaultDatePeriodToLabel(defaultDatePeriod) })),
            display: surveyTemporalFilterType === SurveyTemporalFilterType.DATE,
            column: 1,
            isOptional: false,
        },
        {
            id: 'surveyHasDashboard',
            label: t('surveys.modal.createSurveyForm.surveyHasDashboard'),
            placeholder: undefined,
            value: surveyHasDashboard,
            onChange: handleChangeSurveyHasDashboard,
            type: FormTypeEnum.RADIO_SELECT,
            choices: [
                { id: true, label: t('form.yes') },
                { id: false, label: t('form.no') },
            ],
            display: true,
            column: 1,
            isOptional: false,
        },
        {
            id: 'surveyHasHotAlert',
            label: t('surveys.modal.createSurveyForm.surveyHasHotAlert'),
            placeholder: undefined,
            value: surveyHasHotAlert,
            onChange: handleChangeSurveyHasHotAlert,
            type: FormTypeEnum.RADIO_SELECT,
            choices: [
                { id: true, label: t('form.yes') },
                { id: false, label: t('form.no') },
            ],
            display: true,
            column: 2,
            isOptional: false,
        },
        {
            id: 'surveyHasAutomaticSynchronization',
            label: t('surveys.modal.createSurveyForm.surveyHasAutomaticSynchronization'),
            placeholder: undefined,
            value: surveyHasAutomaticSynchronization,
            onChange: handleChangeSurveyHasAutomaticSynchronization,
            type: FormTypeEnum.RADIO_SELECT,
            choices: [
                { id: true, label: t('form.yes') },
                { id: false, label: t('form.no') },
            ],
            display: true,
            column: 2,
            isOptional: false,
            information: t('surveys.modal.createSurveyForm.information.surveyHasAutomaticSynchronization'),
        },
        {
            id: 'surveyHasToAnonymateData',
            label: t('surveys.modal.createSurveyForm.surveyHasToAnonymateData'),
            placeholder: undefined,
            value: surveyHasToAnonymateData,
            onChange: handleChangeSurveyHasToAnonymateData,
            type: FormTypeEnum.RADIO_SELECT,
            choices: [
                { id: true, label: t('form.yes') },
                { id: false, label: t('form.no') },
            ],
            display: true,
            column: 2,
            isOptional: false,
            information: t('surveys.modal.createSurveyForm.information.surveyHasToAnonymateData'),
        },
        {
            id: 'surveyAnonymateDays',
            label: t('surveys.modal.createSurveyForm.surveyAnonymateDays'),
            placeholder: undefined,
            value: surveyAnonymateDays,
            onChange: handleChangeSurveyAnonymateDays,
            type: FormTypeEnum.NUMBER,
            display: surveyHasToAnonymateData,
            column: 2,
            isOptional: false,
        },
    ];

    return (
        <Form formGroups={formGroups} handleNextStep={handleNextStep} id="#step2" title="Personnalisation" />
    );
};

export default StepPersonalizeSurvey;
